/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { BenefitType, NetworkStatus, ReferentPriceType, USState } from '@strata/constants';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import dayjs from 'dayjs';

import { REFERENCE_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface PayerSegment {
    id: string;
    name: string;
    payerId: string;
}

export interface ConfiguredProductForPayer {
    oid: string;
    description: string;
    brandProductDescription: string;
    genericProductDescription: string;
}

export interface ProductConfig {
    productServiceOid: string;
    hcpcsBillingLabel: string;
    ndcBillingLabel: string;
    multistrengthFlag: boolean;
    preferredBillingType: BenefitType;
}

export interface SupplierDetails {
    npi: string;
    name: string;
    phone?: string;
    fax?: string;
    email?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: USState;
    zip?: string;
    networkStatus: NetworkStatus;
    taxonomyCodes: { name: string; secondaryData: string }[];
}

export interface DiagnosisCodeOption {
    description: string;
    code: string;
    name: string;
    secondaryData: string;
}

export interface ProductCodeOption {
    codesystem: string;
    code: string;
    oid: string;
    description: string;
    name: string;
    secondaryData: string;
    isBrand: boolean;
}

export const referenceApi = createApi({
    baseQuery: getBaseQuery(REFERENCE_API_URL),
    reducerPath: 'referenceApi',
    endpoints: (build) => ({
        getPayerSegmentsForSupplier: build.query<PayerSegment[], { supplierNpi: string }>({
            query: ({ supplierNpi }) => `payer_segments/forsupplier/${supplierNpi}`,
            transformResponse: (
                response: { segment_id: string; segment_name: string; payer_id: string }[],
            ) =>
                response
                    .map((segment) => ({
                        id: segment.segment_id,
                        name: segment.segment_name,
                        payerId: segment.payer_id,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError(
                `An error occurred retrieving health plans for supplier.`,
            ),
        }),
        /** Gets list of payer segments */
        getPayerSegments: build.query<
            { payerSegmentList: PayerSegment[]; payerSegmentMap: Record<string, PayerSegment> },
            void
        >({
            query: () => 'payer_segments',
            transformResponse: (
                response: { segment_id: string; segment_name: string; payer_id: string }[],
            ) => {
                const payerSegmentList = response
                    .map((segment) => ({
                        id: segment.segment_id,
                        name: segment.segment_name,
                        payerId: segment.payer_id,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name));
                const payerSegmentMap = Object.fromEntries(
                    payerSegmentList.map((segment) => [segment.id, segment]),
                );
                return { payerSegmentList, payerSegmentMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving health plans.',
            ),
        }),
        getProductsMap: build.query<
            Record<
                string,
                { description: string; brandDescription: string; genericDescription: string }
            >,
            void
        >({
            query: () => '/productservices/summaryonly',
            transformResponse: (
                response: {
                    oid: string;
                    description: string;
                    brand_product_description: string;
                    generic_product_description: string;
                }[],
            ) =>
                Object.fromEntries(
                    (response ?? []).map((product) => [
                        product.oid,
                        {
                            description: product.description,
                            brandDescription: product.brand_product_description,
                            genericDescription: product.generic_product_description,
                        },
                    ]),
                ),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving products.'),
        }),
        getProductCodeMap: build.query<Record<string, ProductCodeOption>, void>({
            query: () => 'productservices/',
            transformResponse: (
                response: {
                    oid: string;
                    intake?: boolean;
                    description?: string;
                    generic_product_description?: string;
                    brand_product_description?: string;
                    codes: {
                        codesystem: string;
                        code: string;
                        description: string;
                        is_brand: boolean;
                    }[];
                }[],
            ) =>
                Object.fromEntries(
                    (response ?? [])
                        .map((med) =>
                            med.codes.map<[string, ProductCodeOption]>((code) => [
                                code.code,
                                {
                                    code: code.code,
                                    description: code.description,
                                    codesystem: code.codesystem,
                                    oid: med.oid,
                                    name: code.description,
                                    secondaryData: code.code,
                                    isBrand: code.is_brand,
                                },
                            ]),
                        )
                        .flat()
                        .sort(([a], [b]) => sortAsc(a, b)),
                ),
            transformErrorResponse: handleRTKQueryError('Error retrieving medication details.'),
        }),
        /** Gets list of configured products for a payer. */
        getConfiguredProductsForPayer: build.query<
            ConfiguredProductForPayer[],
            { segmentId: string }
        >({
            query: ({ segmentId }) => `product_benefit_configs/segments/${segmentId}`,
            transformResponse: (
                response: {
                    product_service_oid: string;
                    description: string;
                    brand_product_description: string;
                    generic_product_description: string;
                }[],
            ) =>
                response
                    .map((product) => ({
                        oid: product.product_service_oid,
                        description: product.description,
                        brandProductDescription: product.brand_product_description,
                        genericProductDescription: product.generic_product_description,
                    }))
                    .sort((a, b) => sortAsc(a.description, b.description)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving configured products for health plan.',
            ),
        }),
        getProductConfigForPayer: build.query<
            ProductConfig[],
            { segmentId: string; productServiceOid: string }
        >({
            query: ({ segmentId, productServiceOid }) =>
                `product_benefit_configs/${segmentId}/${productServiceOid}`,
            transformResponse: (
                response: {
                    product_service_oid: string;
                    hcpcs_billing_label: string;
                    ndc_billing_label: string;
                    multistrength_flag: boolean;
                    preferred_billing_type: BenefitType;
                }[],
            ) =>
                response.map((config) => ({
                    hcpcsBillingLabel: config.hcpcs_billing_label,
                    multistrengthFlag: config.multistrength_flag,
                    ndcBillingLabel: config.ndc_billing_label,
                    preferredBillingType: config.preferred_billing_type,
                    productServiceOid: config.product_service_oid,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving product configuration for health plan.',
            ),
        }),
        getSupplierDetails: build.query<SupplierDetails, { npi: string }>({
            query: ({ npi }) => `suppliers/${npi}`,
            transformResponse: (response: {
                supplier_npi: string;
                organization_name: string;
                address_1?: string;
                address_2?: string;
                city?: string;
                state?: USState;
                postal_code?: string;
                telephone_number?: string;
                fax_number?: string;
                email?: string;
                network_status: NetworkStatus;
                extra_taxonomy_codes: { taxonomy_code: string; taxonomy_description: string }[];
            }) => ({
                name: response?.organization_name,
                phone: response?.telephone_number,
                fax: response?.fax_number,
                email: response?.email,
                address1: response?.address_1,
                address2: response?.address_2,
                city: response?.city,
                state: response?.state,
                zip: response?.postal_code,
                npi: response?.supplier_npi,
                networkStatus: response?.network_status,
                taxonomyCodes:
                    response.extra_taxonomy_codes?.map((code) => ({
                        name: code.taxonomy_code,
                        secondaryData: code.taxonomy_description,
                    })) ?? [],
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving pharmacy details.', err);
                }
            },
        }),
        // gets diagnosis object
        getDiagnosis: build.query<
            {
                oid: string;
                description: string;
                codes: {
                    codesystem: string;
                    code: string;
                    description: string;
                    isBrand: boolean;
                }[];
            },
            { diagnosisOid: string }
        >({
            query: ({ diagnosisOid }) => `diagnoses/${diagnosisOid}`,
            transformResponse: (response: {
                oid: string;
                description: string;
                codes: {
                    codesystem: string;
                    code: string;
                    description: string;
                    is_brand: boolean;
                }[];
            }) => ({
                oid: response.oid,
                description: response.description,
                codes: response.codes.map((code) => ({
                    codesystem: code.codesystem,
                    code: code.code,
                    description: code.description,
                    isBrand: code.is_brand,
                })),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving diagnosis.'),
        }),
        getDiagnosesCodes: build.query<DiagnosisCodeOption[], void>({
            query: () => 'diagnoses/codes',
            transformResponse: (
                response: { codesystem: string; code: string; description: string }[],
            ) =>
                response?.map((code) => ({
                    description: code.description,
                    code: code.code,
                    // Used for selects
                    name: code.description,
                    secondaryData: code.code,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving diagnoses codes.',
            ),
        }),
        getPlaceOfServiceMap: build.query<Record<string, string>, void>({
            query: () => 'product_benefit_configs/places',
            transformResponse: (response: { code: string; description: string }[]) =>
                Object.fromEntries(
                    (response ?? []).map((option) => [option.code, option.description]),
                ),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving place of service options.',
            ),
        }),
        postClaimEstimateCost: build.query<
            { claim_fact: string; unit_price: string; qty: string },
            {
                productServiceOid: string;
                dateOfService: dayjs.ConfigType;
                referralStart: dayjs.ConfigType;
                priceSource: string;
                priceIsFrozen: boolean;
                qty: number;
                bidAmt: number;
                referentPriceType: ReferentPriceType;
                ndc?: string;
                hcpcsCode: string;
            }
        >({
            query: ({
                productServiceOid,
                dateOfService,
                referralStart,
                priceSource,
                priceIsFrozen,
                qty,
                bidAmt,
                referentPriceType,
                ndc,
                hcpcsCode,
            }) => ({
                url: 'prices/medical_claim_fact',
                method: 'POST',
                body: {
                    product_service_oid: productServiceOid,
                    date_of_service: formatDate(dateOfService, DateFormats.HYPHEN_DATE),
                    referral_start: formatDate(referralStart, DateFormats.HYPHEN_DATE),
                    price_source: priceSource,
                    price_is_frozen: priceIsFrozen,
                    qty,
                    bid_amt: bidAmt,
                    referent_price_type: referentPriceType,
                    ndc,
                    hcpcs_code: hcpcsCode,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred calculating estimated provider charge.',
            ),
        }),
        getEligiblePricesForOid: build.query<
            { code: string; price: string }[],
            {
                oid: string;
                referentPriceType: ReferentPriceType;
                isBrand: boolean;
                priceSource: string;
                priceDate?: dayjs.ConfigType;
                benefitType?: BenefitType;
            }
        >({
            query: ({ oid, referentPriceType, isBrand, priceSource, priceDate, benefitType }) => ({
                url: `prices/oid/${oid}/eligible_prices`,
                params: {
                    referent_price_type: referentPriceType,
                    is_brand: isBrand,
                    price_source: priceSource,
                    price_date: formatDate(priceDate, DateFormats.HYPHEN_DATE),
                    benefit_type: benefitType,
                },
            }),
            transformResponse: (response: { code: string; price: string }[]) =>
                response?.map((price) => ({ code: price.code, price: price.price })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving unit price info.',
            ),
        }),
    }),
});

export const {
    useGetPayerSegmentsForSupplierQuery,
    useGetProductsMapQuery,
    useGetProductCodeMapQuery,
    useGetProductConfigForPayerQuery,
    useGetConfiguredProductsForPayerQuery,
    useGetSupplierDetailsQuery,
    useGetDiagnosesCodesQuery,
    useGetPlaceOfServiceMapQuery,
    usePostClaimEstimateCostQuery,
    useGetEligiblePricesForOidQuery,
    useGetPayerSegmentsQuery,
    useGetDiagnosisQuery,
} = referenceApi;
